import { monthlyPaymentEur } from '@/lib/financingEstimate';
import { useFxRates } from '@/lib/fxRates';
import { canDirectContact } from '@/utils/dealerContactGate';
import { supabase } from '@/integrations/supabase/client';
import { trackContactClick } from '@/utils/contactTracking';
import { Link } from "@/lib/router-compat";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  Phone, 
  MapPin, 
  Calendar, 
  Gauge, 
  GasPump, 
  GearSix, 
  Ruler, 
  Anchor, 
  Clock,
  ShieldCheck,
  ArrowsLeftRight
} from "@phosphor-icons/react";
import type { ListingV2Response } from "@/types/listings-v2";
import { MILEAGE_LABELS } from "@/types/listings-v2";
import { FavoriteButton } from "@/components/listings/card/FavoriteButton";
import { useCurrencyConversion } from "@/hooks/useCurrencyConversion";
import { CountrySpecificAttributes } from "@/components/listings/forms/CountrySpecificAttributes";
import { useTranslation } from "@/hooks/useTranslation";
import { useCountry } from "@/hooks/useCountry";
import { convertDistance } from "@/utils/countryConfig";
import { localizeFuel, localizeTransmission } from "@/utils/listingLocalization";
import { memo, useState } from "react";
import { COUNTRY_ROUTES } from "@/utils/countryRoutes";
import { AspectAwareImage } from "@/components/ui/AspectAwareImage";
import { CarVerticalButton } from "@/components/listings/detail/CarVerticalButton";
import { getFinancingEligibility } from "@/components/listings/detail/FinancingCTA";
import { selectBestCardImage, hasValidPhoto } from "@/utils/imageSelection";
import { WhatsAppCardButton } from "@/components/listings/detail/WhatsAppCardButton";
import { CardDealerStrip } from "@/components/dealers/CardDealerStrip";
import { PremiumDealerBadge } from "@/components/dealers/PremiumDealerBadge";
import { PriceBenchmarkBadge } from "@/components/vehicles/PriceBenchmarkBadge";
import { usePriceBenchmark } from "@/hooks/usePriceBenchmark";
import { formatMakeDisplay } from "@/utils/makeDisplay";

interface ListingCardV2Props {
  listing: ListingV2Response;
  onCompareToggle?: (id: string) => void;
  isInComparison?: boolean;
  priority?: boolean; // Whether to load image with priority (eager)
}

const ListingCardV2Component = ({ 
  listing, 
  onCompareToggle,
  isInComparison = false,
  priority = false
}: ListingCardV2Props) => {
  // Smart image selection: prefer exterior shots, avoid interior/dashboard
  const mainImage = selectBestCardImage(listing.photos || [], null);
  const hasImages = hasValidPhoto(mainImage);
  const [imageAspect, setImageAspect] = useState<number | null>(null);
  const { formatPrice } = useCurrencyConversion();
  const { data: fxRates } = useFxRates();
  const { t, language } = useTranslation();
  const { config, country } = useCountry();
  const status = (listing as any).status ?? listing.attributes?.status;
  const { label: priceLabel, diffPct: priceDiff, benchmark } = usePriceBenchmark({
    make: listing.make,
    model: listing.model,
    year: listing.year,
    mileage: (listing.mileage_unit === "km" || !listing.mileage_unit) ? listing.mileage_value ?? null : null,
    price: listing.price,
    currency: listing.currency,
    countryCode: (listing as any).country_code ?? "RO",
  });
  
  // Country flag mapping
  const countryFlags: Record<string, string> = {
    'RO': '🇷🇴',
    'UK': '🇬🇧',
    'DE': '🇩🇪'
  };
  
  // Normalize various codes/names to our internal CountryCode
  const normalizeCountry = (code?: string, countryName?: string): 'RO' | 'UK' | 'DE' | undefined => {
    const c = (code || '').toUpperCase();
    if (c === 'RO' || c === 'UK' || c === 'DE') return c as any;
    if (c === 'GB') return 'UK';
    const name = (countryName || '').toLowerCase();
    if (['united kingdom','uk','great britain','england','scotland','wales','northern ireland'].includes(name)) return 'UK';
    if (['germany','deutschland','de'].includes(name)) return 'DE';
    if (['romania','românia','ro'].includes(name)) return 'RO';
    return undefined;
  };
  
  // Derive country only from listing data (no UI fallback)
  const displayCountryCode = normalizeCountry(listing.country_code, listing.location_country);
  const showCountryBadge = true;
  
  // Convert mileage if needed
  const mileageUnit = listing.mileage_unit || 'km';
  const displayMileage = listing.mileage_value 
    ? (mileageUnit === 'km' || mileageUnit === 'miles')
      ? convertDistance(listing.mileage_value, mileageUnit, config.distanceUnit)
      : listing.mileage_value
    : null;
  const mileageLabel = (mileageUnit === 'km' || mileageUnit === 'miles') 
    ? config.distanceUnit 
    : mileageUnit;

  const formatMileage = (value?: number) => {
    if (!value) return 'N/A';
    return new Intl.NumberFormat(config.locale).format(value);
  };

  const handleContact = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (!(await canDirectContact((listing as any).user_id))) {
      window.location.href = COUNTRY_ROUTES.listing(country, listing.id);
      return;
    }
    // List data never carries the phone; fetch it the same way as the other cards.
    let phone: string | null | undefined = listing.contact_phone;
    if (!phone) {
      try {
        const { data } = await supabase.rpc('get_public_contact_info', { _listing_id: listing.id });
        phone = (data as any)?.[0]?.contact_phone ?? null;
      } catch { phone = null; }
    }
    if (!phone) {
      window.location.href = COUNTRY_ROUTES.listing(country, listing.id);
      return;
    }
    if (phone) {
      trackContactClick({ id: listing.id, sellerId: (listing as any).user_id, sellerType: (listing as any).seller_type }, 'phone', 'list_card');
      window.location.href = `tel:${phone}`;
    }
  };

  return (
    <Card className="overflow-hidden border-border bg-card hover:border-primary/40 transition-colors duration-200 group">
      <div className="relative">
        <Link to={COUNTRY_ROUTES.listing(country, listing.id)} className="block">
          <div className="relative">
            {hasImages ? (
              <AspectAwareImage
                src={mainImage!}
                alt={`${listing.make} ${listing.model}`}
                grayscale={status === 'sold'}
                width={400}
                height={300}
                priority={priority}
              />
            ) : (
              <div className="aspect-[4/3] w-full flex flex-col items-center justify-center bg-gradient-to-br from-muted to-muted/50 text-muted-foreground">
                <svg className="w-16 h-16 mb-2 opacity-40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span className="text-sm font-medium">Imagini în curând</span>
              </div>
            )}
            
          </div>
        </Link>

        {/* Sold banner overlay */}
        {status === 'sold' && (
          <div className="absolute inset-0 z-30 flex items-center justify-center bg-muted/80 backdrop-blur-sm pointer-events-none">
            <Badge
              variant="destructive"
              className="px-6 py-3 text-base md:text-lg font-black tracking-widest shadow-2xl rotate-12 border border-background/40"
            >
              VÂNDUT
            </Badge>
          </div>
        )}

        {/* Badges */}
        <div className="absolute top-3 left-3 flex flex-wrap gap-2 z-10">
          {status === 'reserved' && (
            <Badge className="bg-amber-500/90 text-amber-950 backdrop-blur-sm shadow-md font-semibold text-[11px] px-2 py-0.5">
              {t('status.reserved')}
            </Badge>
          )}
          {status === 'sold' && (
            <Badge className="bg-muted/90 text-muted-foreground backdrop-blur-sm shadow-md font-semibold text-[11px] px-2 py-0.5">
              {t('status.sold')}
            </Badge>
          )}
          {listing.is_new_listing && (
            <Badge variant="secondary" className="bg-accent text-accent-foreground shadow-md text-[11px] px-2 py-0.5">
              {country === 'RO' ? 'Anunț nou' : t('status.new')}
            </Badge>
          )}
          {listing.condition === 'new' && !listing.is_new_listing && (
            <Badge className="bg-green-500/90 text-white backdrop-blur-sm text-[11px] px-2 py-0.5">
              {t('status.new')}
            </Badge>
          )}
        </div>

        {/* Favorite Button */}
        <div className="absolute top-3 right-3">
          <FavoriteButton listingId={listing.id} />
        </div>
      </div>

      <CardDealerStrip
        userId={listing.user_id}
        listingCity={listing.location_city}
        dealerVerified={listing.dealer_verified}
      />

      <CardContent className="p-4">
        <Link to={COUNTRY_ROUTES.listing(country, listing.id)} className="block group">
          {/* Title + Year */}
          <h3 className="text-base md:text-lg font-bold text-foreground group-hover:text-primary transition-colors line-clamp-1 mb-1 tracking-tight">
            {formatMakeDisplay(listing.make)} {listing.model}{`${listing.make} ${listing.model} ${listing.trim_level || ''}`.length > 40 ? ` ${listing.year}` : listing.trim_level && !['null', 'undefined', 'NULL'].includes(String(listing.trim_level).trim()) ? ` ${listing.trim_level}` : ''}
          </h3>

          {/* Price - firm but not aggressive */}
          <div className="mb-3">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-serif text-xl md:text-2xl font-semibold text-primary tracking-tight">
                {formatPrice(listing.price, listing.currency)}
              </span>
              {priceLabel && (
                <PriceBenchmarkBadge
                  label={priceLabel}
                  diffPct={priceDiff}
                  sampleSize={benchmark?.sample_size}
                />
              )}
            </div>
            {(() => {
              const ronRate = fxRates?.RON;
              if (!ronRate) return null;
              const priceEur = listing.currency === 'EUR'
                ? listing.price
                : listing.currency === 'RON'
                  ? listing.price / ronRate
                  : null;
              if (!priceEur) return null;
              const mileageKm = (listing.mileage_unit === 'km' || !listing.mileage_unit)
                ? listing.mileage_value ?? undefined
                : undefined;
              const eligibility = getFinancingEligibility(listing.year, mileageKm, priceEur);
              if (!eligibility) return null;
              const priceRon = priceEur * ronRate;
              const rataEur = Math.round(monthlyPaymentEur(priceEur));
              const carTitle = [listing.make, listing.model, listing.trim_level].filter(Boolean).join(' ');
              const fullTitle = listing.year ? `${carTitle} ${listing.year}` : carTitle;
              const fparams = new URLSearchParams({
                brand: listing.make || '',
                make: listing.make || '',
                marca: listing.make || '',
                model: listing.model || '',
                trim: listing.trim_level || '',
                versiune: listing.trim_level || '',
                year: String(listing.year || ''),
                an: String(listing.year || ''),
                price: String(Math.round(priceRon)),
                pret: String(Math.round(priceRon)),
                currency: 'RON',
                fuel: listing.attributes?.fuel || '',
                combustibil: listing.attributes?.fuel || '',
                mileage: String(mileageKm || ''),
                km: String(mileageKm || ''),
                listingId: listing.id || '',
                product: eligibility,
                title: fullTitle,
                car: fullTitle,
                vehicul: fullTitle,
                masina: fullTitle,
                descriere: fullTitle,
                utm_source: 'roautomarket',
                utm_medium: 'card',
                utm_campaign: `card_${eligibility}`,
              });
              const financingUrl = `https://www.autocompare.ro/aplica-finantare?${fparams.toString()}`;
              const badgeLabel = eligibility === 'leasing'
                ? `Leasing de la ${rataEur} €/lună`
                : `Credit de la ${rataEur} €/lună`;
              return (
                <div className="mt-1.5 flex flex-col gap-1">
                  {/* span, nu <a>: cardul e deja un link, iar <a> în <a> strică hidratarea */}
                  <span
                    role="link"
                    tabIndex={0}
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open(financingUrl, '_blank', 'noopener,noreferrer'); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); window.open(financingUrl, '_blank', 'noopener,noreferrer'); } }}
                    className="inline-flex w-fit cursor-pointer text-primary text-sm font-semibold hover:underline"
                  >
                    {badgeLabel}
                  </span>
                </div>
              );
            })()}
          </div>

          {/* Specs Grid - Different layout based on category */}
          {listing.category_code === 'boat' || listing.category_code === 'yacht' || listing.category_code === 'jet_ski' ? (
            // Boats layout
            <div className="grid grid-cols-2 gap-2 mb-3 text-sm">
              <div className="flex items-center gap-1.5 text-foreground text-sm font-medium">
                <Calendar className="w-4 h-4 text-primary" weight="duotone" />
                <span>{listing.year}</span>
              </div>
              
              {listing.attributes?.length_m && (
                <div className="flex items-center gap-1.5 text-foreground text-sm font-medium">
                  <Ruler className="w-4 h-4 text-primary" weight="duotone" />
                  <span>{listing.attributes.length_m}m</span>
                </div>
              )}

              {listing.attributes?.beam_m && (
                <div className="flex items-center gap-1.5 text-foreground text-sm font-medium">
                  <Anchor className="w-4 h-4 text-primary" weight="duotone" />
                  <span>L: {listing.attributes.beam_m}m</span>
                </div>
              )}

              {listing.attributes?.engine_power_hp && (
                <div className="flex items-center gap-1.5 text-foreground text-sm font-medium">
                  <GearSix size={16} weight="duotone" className="text-primary" />
                  <span>{listing.attributes.engine_power_hp} CP</span>
                </div>
              )}

              {listing.attributes?.hull_material && (
                <div className="flex items-center gap-1.5 text-foreground text-sm font-medium">
                  <Gauge className="w-4 h-4 text-primary" weight="duotone" />
                  <span className="truncate">{listing.attributes.hull_material}</span>
                </div>
              )}

              {listing.mileage_value && mileageUnit === 'hours' && (
                <div className="flex items-center gap-1.5 text-foreground text-sm font-medium">
                  <Clock className="w-4 h-4 text-primary" weight="duotone" />
                  <span>{new Intl.NumberFormat('ro-RO').format(listing.mileage_value)} ore</span>
                </div>
              )}
            </div>
          ) : listing.category_code === 'tractor' || listing.category_code === 'harvester' || listing.category_code === 'agricultural_machinery' || listing.category_code === 'construction_machinery' ? (
            // Agricultural/Construction machinery layout
            <div className="grid grid-cols-2 gap-2 mb-3 text-sm">
              <div className="flex items-center gap-1.5 text-foreground text-sm font-medium">
                <Calendar className="w-4 h-4 text-primary" weight="duotone" />
                <span>{listing.year}</span>
              </div>

              {listing.mileage_value && (
                <div className="flex items-center gap-1.5 text-foreground text-sm font-medium">
                  <Clock className="w-4 h-4 text-primary" weight="duotone" />
                  <span>{new Intl.NumberFormat('ro-RO').format(listing.mileage_value)} ore</span>
                </div>
              )}

              {listing.attributes?.power_hp && (
                <div className="flex items-center gap-1.5 text-foreground text-sm font-medium">
                  <GearSix size={16} weight="duotone" className="text-primary" />
                  <span>{listing.attributes.power_hp} CP</span>
                </div>
              )}

              {listing.attributes?.implement_type && (
                <div className="flex items-center gap-1.5 text-foreground text-sm font-medium">
                  <Gauge className="w-4 h-4 text-primary" weight="duotone" />
                  <span className="truncate">{listing.attributes.implement_type}</span>
                </div>
              )}
            </div>
          ) : (
            // Default cars/vehicles layout
            <div className="grid grid-cols-2 gap-2 mb-3 text-sm">
              <div className="flex items-center gap-1.5 text-foreground text-sm font-medium">
                <Calendar className="w-4 h-4 text-primary" weight="duotone" />
                <span>{listing.year}</span>
              </div>
              
              {displayMileage !== null && (
                <div className="flex items-center gap-1.5 text-foreground text-sm font-medium">
                  <Gauge className="w-4 h-4 text-primary" weight="duotone" />
                  <span>{formatMileage(displayMileage)} {mileageLabel}</span>
                </div>
              )}

              {listing.attributes?.fuel && (
                <div className="flex items-center gap-1.5 text-foreground text-sm font-medium">
                  <GasPump size={16} weight="duotone" className="text-primary" />
                  <span className="truncate">{localizeFuel(listing.attributes.fuel, language)}</span>
                </div>
              )}

              {listing.attributes?.transmission && (
                <div className="flex items-center gap-1.5 text-foreground text-sm font-medium">
                  <GearSix size={16} weight="duotone" className="text-primary" />
                  <span className="truncate">{localizeTransmission(listing.attributes.transmission, language)}</span>
                </div>
              )}
            </div>
          )}

          {/* Location */}
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground mb-3">
            <MapPin className="w-4 h-4 flex-shrink-0 text-primary/70" weight="fill" />
            <span className="truncate">
              {[
                listing.location_city, 
                listing.location_region, 
                displayCountryCode === 'RO' ? 'România' : 
                displayCountryCode === 'UK' ? 'Marea Britanie' : 
                displayCountryCode === 'DE' ? 'Germania' : 
                listing.location_country
              ].filter(Boolean).join(', ')}
            </span>
          </div>
        </Link>

        {/* Action buttons - CarVertical & Cost moved to detail page per audit */}
        <div className="pt-2 border-t border-border/50 flex items-center gap-1.5 min-w-0">
          <Button
            onClick={handleContact}
            size="sm"
            className="bronze-action flex-1 min-w-0 gap-1 font-semibold text-xs sm:text-sm px-2 whitespace-nowrap"
            variant="default"
          >
            <Phone className="w-4 h-4 flex-shrink-0" />
            <span className="truncate">{country === 'UK' ? 'Call' : country === 'DE' ? 'Anrufen' : 'Sună'}</span>
          </Button>
          <WhatsAppCardButton
            listingId={listing.id}
            listingTitle={`${listing.make} ${listing.model} ${listing.year}`}
            sellerId={(listing as any).user_id}
            size="sm"
            className="flex-1 min-w-0 px-2 whitespace-nowrap"
          />


          {onCompareToggle && (
            <Button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onCompareToggle(listing.id); }}
              data-compare-selected={isInComparison}
              aria-label={isInComparison ? `Elimină ${listing.make} ${listing.model} din comparație` : `Adaugă ${listing.make} ${listing.model} în comparație`}
              aria-pressed={isInComparison}
              variant="outline"
              size="sm"
              className={`min-h-9 inline-flex items-center justify-center gap-1 transition-colors text-xs border ${
                isInComparison
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-background text-muted-foreground border-border hover:bg-muted hover:text-foreground'
              }`}
            >
              <ArrowsLeftRight size={14} aria-hidden="true" /> Compară
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Memoize component to prevent unnecessary re-renders
export const ListingCardV2 = memo(ListingCardV2Component);
